import axios from 'axios';
import {ENDPOINT_URL, GET_ROLES_LIST} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a Canvas role row identifier.
 * @param {object} item Canvas role row
 * @return {*} identifier
 */
function roleId(item) {
	return item.id ?? item.role_id;
}

/**
 * Normalize a Canvas role row.
 * @param {object} item Canvas role row
 * @return {object|null} agent candidate
 */
function normalizeRole(item) {
	const id = roleId(item);
	const name = item.name ?? item.role_name;
	if (id == null || !name) return null;
	return {
		entity_type: 'role',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

// Generic words that mean "no role was actually named" (e.g. "edit role"
// with nothing left over to search for) rather than a real, if partial,
// role name. Left unfiltered, a bare "role" is a valid substring match
// against any role literally named e.g. "WAN Role" - a false positive
// that looks like a resolved target but isn't one the person named.
const GENERIC_ROLE_QUERIES = new Set(['role', 'roles', 'a role', 'the role', 'any role']);

/**
 * Resolve Canvas role candidates.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveRoles(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	if (GENERIC_ROLE_QUERIES.has(query.toLowerCase())) {
		throw new Error('no_target_specified');
	}
	const loadRows = async (search, limit) => {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${GET_ROLES_LIST}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('role', rows, roleId);
		return {
			candidates: rows.map(normalizeRole).filter(Boolean).slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow: normalizeRole,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('role', rows, roleId);
	return {candidates};
}

/**
 * Return a retained Canvas role row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedRole(entityId) {
	return getResolvedEntity('role', entityId);
}
